import React, { useEffect, useState } from "react";
import {
  AiOutlineDelete,
  AiOutlineEye,
  AiOutlineUserAdd,
  AiOutlineSearch,
} from "react-icons/ai";
import { FaUsers } from "react-icons/fa6"; // Using FaUsers for consistency with MemberManagement
import { Link, useNavigate } from "react-router-dom"; // Import useNavigate
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { RotatingLines } from "react-loader-spinner"; // Used for main loading
import { useAuth } from "../../context/Auth"; // Use useAuth hook for requestWithAuth
import Header from "../../components/Header";
import { useGlobalContext } from "../../context/stateContext";
import RoleBasedComponent from "../../components/RoleBasedComponent";

const VisitorsList = () => {
  const navigate = useNavigate();
  const { requestWithAuth } = useAuth();
  const { user } = useGlobalContext();

  const [visitors, setVisitors] = useState([]);
  const [loadingVisitors, setLoadingVisitors] = useState(false);
  const [deletingVisitorId, setDeletingVisitorId] = useState(null); // State for delete loading
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10); // Consistent page size with MemberManagement
  const [search, setSearch] = useState("");
  const [totalPages, setTotalPages] = useState(0);

  const getVisitors = async (searchValue, currentPage, pageSize) => {
    try {
      setLoadingVisitors(true);

      const queryParams = new URLSearchParams({
        search: searchValue,
        page_size: pageSize,
        page: currentPage,
      }).toString();

      const response = await requestWithAuth(
        `/members/visitors/list?${queryParams}`,
        "GET"
      );
      setVisitors(response.results); 
      setTotalPages(Math.ceil(response.count / pageSize)); 
    } catch (error) {
      console.error("Failed to fetch visitors:", error);
      toast.error("Failed to load visitors. Please try again.");
      setVisitors([]); // Clear visitors on error
      setTotalPages(0);
    } finally {
      setLoadingVisitors(false);
    }
  };

  // Effect to fetch visitors on initial load and whenever search, pageSize, or currentPage changes
  useEffect(() => {
    getVisitors(search, currentPage, pageSize);
  }, [search, currentPage, pageSize]);

  // Function to initiate delete confirmation
  const initializeDeleteVisitor = (visitorId) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then((result) => {
      if (result.isConfirmed) {
        deleteVisitor(visitorId);
      }
    });
  };

  // Function to handle visitor deletion via API
  const deleteVisitor = async (visitorId) => {
    setDeletingVisitorId(visitorId); // Set the ID of the visitor being deleted to show loading
    try {
      await requestWithAuth(`/members/${visitorId}/`, "DELETE");
      toast.success("Visitor deleted successfully!");

      getVisitors(search, currentPage, pageSize);
    } catch (error) {
      console.error("Failed to delete visitor:", error);
      const errorMessage =
        error.response?.data?.detail ||
        "Failed to delete visitor. Please try again.";
      toast.error(errorMessage);
    } finally {
      setDeletingVisitorId(null); // Clear the deleting state
    }
  };

  // Pagination Handlers (Copied from MemberManagement for consistency)
  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handlePageClick = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Render page numbers for pagination (Copied from MemberManagement for consistency)
  const renderPageNumbers = () => {
    const pageNumbers = [];
    const totalNumbersToShow = 3; // Number of visible page buttons around current page

    let startPage = Math.max(
      1,
      currentPage - Math.floor(totalNumbersToShow / 2)
    );
    let endPage = Math.min(totalPages, startPage + totalNumbersToShow - 1);

    // Adjust start and end if at the boundaries
    if (endPage - startPage + 1 < totalNumbersToShow) {
      startPage = Math.max(1, endPage - totalNumbersToShow + 1);
    }

    if (startPage > 1) {
      pageNumbers.push(1);
      if (startPage > 2) {
        pageNumbers.push("...");
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pageNumbers.push("...");
      }
      pageNumbers.push(totalPages);
    }

    return pageNumbers.map((number, index) => (
      <button
        key={index}
        onClick={() => number !== "..." && handlePageClick(number)}
        disabled={number === "..."}
        className={`
                    min-w-[40px] h-10 flex items-center justify-center rounded-lg border text-sm font-medium transition-colors
                    ${
                      number === currentPage
                        ? "bg-blue-600 text-white border-blue-600 shadow-sm"
                        : number === "..."
                        ? "bg-white text-gray-400 border-gray-200 cursor-default"
                        : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50 hover:border-gray-300"
                    }
                `}
      >
        {number}
      </button>
    ));
  };

  const getInitials = (fullName) => {
    if (!fullName) return "";
    const names = fullName.split(" ");
    if (names?.length === 1) return names[0][0]?.toUpperCase() || "";
    return `${names[0]?.[0] || ""}${names[1]?.[0] || ""}`.toUpperCase();
  };

  return (
    <div className="min-h-screen bg-gray-50/50 p-6">
      {/* Header Section */}
      <Header title="Visitors List" />
      <RoleBasedComponent allowedRoles={['Administrator','Usher', 'Administrative Assistant']}>
        <Link
            to="/add-visitor"
            className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-lg font-medium transition-colors shadow-sm hover:shadow-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            <AiOutlineUserAdd className="text-lg" />
            Add New Visitor
          </Link>
      </RoleBasedComponent>

      {/* Visitors Table Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mt-5">
        {/* Card Header with Search */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <h2 className="text-lg font-semibold text-gray-900">
              Registered Visitors
            </h2>
            <div className="relative">
              <AiOutlineSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search Visitor by Name"
                value={search}
                onChange={(e) => setSearch(e.target.value)} // Update search state, useEffect will trigger API call
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all w-full sm:w-64"
              />
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          {loadingVisitors ? (
            <div className="flex items-center justify-center py-12">
              <div className="flex flex-col items-center gap-3">
                <RotatingLines
                  visible={true}
                  height="46"
                  width="46"
                  color="grey"
                  strokeWidth="5"
                  animationDuration="0.75"
                  ariaLabel="loading visitors"
                />
                <p className="text-gray-600 text-sm">Loading visitors...</p>
              </div>
            </div>
          ) : visitors?.length > 0 ? (
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="text-left py-3 px-6 font-semibold text-gray-900 text-sm">
                    Visitor Name
                  </th>
                  <th className="text-left py-3 px-6 font-semibold text-gray-900 text-sm">
                    Address
                  </th>
                  <th className="text-left py-3 px-6 font-semibold text-gray-900 text-sm">
                    Email
                  </th>
                  <th className="text-left py-3 px-6 font-semibold text-gray-900 text-sm">
                    Date Registered
                  </th>
                  <th className="text-left py-3 px-6 font-semibold text-gray-900 text-sm">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {visitors.map((visitor) => (
                  <tr
                    key={visitor.id}
                    className="hover:bg-gray-50 transition-colors"
                  >
                    <td className="py-4 px-6">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-400 to-purple-600 flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
                          {getInitials(
                            `${visitor.first_name} ${visitor.last_name}`
                          )}
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-gray-900 truncate">
                            {`${visitor.first_name} ${visitor.last_name}`}
                          </p>
                          <p className="text-sm text-gray-500 truncate">
                            {visitor.phone_number}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="py-4 px-6 text-sm text-gray-700">
                      {visitor?.address}
                    </td>
                    <td className="py-4 px-6 text-sm text-gray-700">
                      {visitor?.email}
                    </td>
                    <td className="py-4 px-6 text-sm text-gray-700">
                      {new Date(visitor.registration_date).toLocaleDateString()}
                    </td>
                    <td className="py-4 px-6">
                      <div className="flex items-center gap-1">
                        <Link
                            to={`/visitors/${visitor?.id}/details`} 
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="View Profile"
                        >
                            <AiOutlineEye className="text-lg" />
                        </Link>
                        <button
                          onClick={() => initializeDeleteVisitor(visitor.id)}
                          disabled={deletingVisitorId === visitor.id}
                          className={`p-2 rounded-lg transition-colors ${
                            deletingVisitorId === visitor.id
                              ? "text-gray-400 bg-gray-100 cursor-not-allowed"
                              : "text-red-600 hover:bg-red-50"
                          }`}
                          title="Delete Visitor"
                        >
                          {deletingVisitorId === visitor.id ? (
                            <RotatingLines
                              visible={true}
                              height="20" // Smaller size for button
                              width="20"
                              color="grey"
                              strokeWidth="5"
                              animationDuration="0.75"
                              ariaLabel="deleting"
                            />
                          ) : (
                            <AiOutlineDelete className="text-lg" />
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 px-6 text-center">
              <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                <FaUsers className="text-gray-400 w-12 h-12" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {search ? "No visitors found" : "No visitors yet"}
              </h3>
              <p className="text-gray-500 text-center mb-6 max-w-md">
                {search
                  ? `No visitors match "${search}". Try adjusting your search.`
                  : "Get started by adding your first visitor to the system."}
              </p>
              {!search &&
                (user?.role === "Administrator" ||
                  user?.role === "Administrative Assistant") && (
                  <Link
                    to="/add-visitor"
                    className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-lg font-medium transition-colors shadow-sm hover:shadow-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  >
                    <AiOutlineUserAdd className="text-lg" />
                    Add First Visitor
                  </Link>
                )}
            </div>
          )}
        </div>

        {/* Pagination */}
        {visitors?.length > 0 && totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50/30">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <p className="text-sm text-gray-700">
                Showing page <span className="font-medium">{currentPage}</span>{" "}
                of <span className="font-medium">{totalPages}</span>
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={handlePreviousPage}
                  disabled={currentPage === 1}
                  className={`px-4 py-2 text-sm font-medium rounded-lg border transition-colors
                                        ${
                                          currentPage === 1
                                            ? "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed"
                                            : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                                        }`}
                >
                  Previous
                </button>

                <div className="flex items-center gap-1">
                  {renderPageNumbers()}
                </div>

                <button
                  onClick={handleNextPage}
                  disabled={currentPage === totalPages}
                  className={`px-4 py-2 text-sm font-medium rounded-lg border transition-colors
                                        ${
                                          currentPage === totalPages
                                            ? "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed"
                                            : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                                        }`}
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VisitorsList;
